import React, { useRef, useState, useEffect } from 'react';
import { useFirestore } from '../contexts/FirestoreContext';
import { Form, Button, Col, Row, FloatingLabel } from 'react-bootstrap';

function CustomerForm({ customer, onCustomerUpdated, onCustomerAdded, onModalClose, registerCleanup }) {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { addData, updateData, getData, queryData } = useFirestore();
  const [buttonsDisabled, setButtonsDisabled] = useState(false);

  const customerNbrRef = useRef();
  const customerNameRef = useRef();
  const customerNotesRef = useRef();
  const cdkCustNbrFordRef = useRef();
  const cdkCustNbrGMRef = useRef();
  const strCustGMFANRef = useRef();
  const strCustFordFINRef = useRef();

  // Address field refs
  const addressLine1Ref = useRef();
  const addressLine2Ref = useRef();
  const cityRef = useRef();
  const stateRef = useRef();
  const zipRef = useRef();
  //const paymentRef = useRef(); // New Ref for Payment dropdown

  useEffect(() => {
    const fetchCustomers = async () => {
      const customerData = await getData('customers');

      if (customer) {
        // Populate form fields if editing an existing customer
        populateCustomerFields(customer);
      } else {
        // Generate new customer number if adding a new customer
        generateNewCustomerId(customerData);
      }
    };

    fetchCustomers();
  }, [customer, getData]);

  // Populate form fields with existing customer data when editing
  const populateCustomerFields = (customer) => {
    if (customerNbrRef.current) customerNbrRef.current.value = customer.strCustNbr || '';
    if (customerNameRef.current) customerNameRef.current.value = customer.strCustName || '';
    if (customerNotesRef.current) customerNotesRef.current.value = customer.lstrCustNotes || '';

    // Populate address fields
    if (addressLine1Ref.current) addressLine1Ref.current.value = customer.strAddressLine1 || '';
    if (addressLine2Ref.current) addressLine2Ref.current.value = customer.strAddressLine2 || '';
    if (cityRef.current) cityRef.current.value = customer.strCity || '';
    if (stateRef.current) stateRef.current.value = customer.strState || '';
    if (zipRef.current) zipRef.current.value = customer.strZip || '';
    //if (paymentRef.current) paymentRef.current.value = customer.strPayment || ''; // Populate Payment

    // Populate CDK Customer Number fields
    if (cdkCustNbrFordRef.current) cdkCustNbrFordRef.current.value = customer.strCDKCustNbrFord || '';
    if (cdkCustNbrGMRef.current) cdkCustNbrGMRef.current.value = customer.strCDKCustNbrGM || '';
    if (strCustFordFINRef.current) strCustFordFINRef.current.value = customer.strCustFordFIN || '';
    if (strCustGMFANRef.current) strCustGMFANRef.current.value = customer.strCustGMFAN || '';
  };

  // Generate new customer number by finding the highest existing one
  const generateNewCustomerId = (customers) => {
    if (!customers || customers.length === 0) {
      customerNbrRef.current.value = "1000";
    } else {
      const maxId = customers.reduce((max, customer) => {
        const customerIdNumber = parseInt(customer.strCustNbr, 10);
        return customerIdNumber > max ? customerIdNumber : max;
      }, 1000);

      customerNbrRef.current.value = (maxId + 1).toString();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const customerNumber = customerNbrRef.current.value;

      // Check if the customer already exists based on number (only if creating new)
      const existingCustomer = await queryData('customers', 'strCustNbr', '==', customerNumber);

      if (existingCustomer.length > 0 && !customer) {
        setError(`Customer with number ${customerNumber} already exists.`);
        setLoading(false);
        return;
      }

      const newCustomer = {
        strCustNbr: customerNumber,
        strCustName: customerNameRef.current.value,
        lstrCustNotes: customerNotesRef.current.value,
        strCDKCustNbrFord: cdkCustNbrFordRef.current.value,
        strCDKCustNbrGM: cdkCustNbrGMRef.current.value,
        strCustFordFIN: strCustFordFINRef.current.value,
        strCustGMFAN: strCustGMFANRef.current.value,

        // Address fields
        strAddressLine1: addressLine1Ref.current.value,
        strAddressLine2: addressLine2Ref.current.value,
        strCity: cityRef.current.value,
        strState: stateRef.current.value,
        strZip: zipRef.current.value,
        //strPayment: paymentRef.current.value // Stored as strPayment in Firebase
      };

      if (customer) {
        // Updates existing document
        await updateData('customers', customer.id, newCustomer);
        onCustomerUpdated(newCustomer);
      } else {
        // Adds new document
        await addData('customers', newCustomer);
        if (onCustomerAdded) {
          onCustomerAdded(newCustomer);
        }
      }

    } catch (err) {
      setError('Failed to save customer');
      console.error("Error updating/adding customer:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-4" style={{ width: '100%', margin: '0 auto' }}>
      {error && <div className="alert alert-danger">{error}</div>}
      <Form onSubmit={handleSubmit}>
        <Row>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3" controlId="customerNbr">
              <FloatingLabel controlId="customerNbr" label="Customer Number">
                <Form.Control type="text" ref={customerNbrRef} disabled={!!customer} />
              </FloatingLabel>
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3" controlId="customerName">
              <FloatingLabel controlId="customerName" label="Customer Name">
                <Form.Control type="text" ref={customerNameRef} />
              </FloatingLabel>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3" controlId="cdkCustNbrFord">
              <FloatingLabel controlId="cdkCustNbrFord" label="CDK Customer Number (FORD)">
                <Form.Control type="text" ref={cdkCustNbrFordRef} />
              </FloatingLabel>
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3" controlId="cdkCustNbrGM">
              <FloatingLabel controlId="cdkCustNbrGM" label="CDK Customer Number (GM)">
                <Form.Control type="text" ref={cdkCustNbrGMRef} />
              </FloatingLabel>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3" controlId="strCustFordFIN">
              <FloatingLabel controlId="strCustFordFIN" label="Ford FIN">
                <Form.Control type="text" ref={strCustFordFINRef} />
              </FloatingLabel>
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3" controlId="strCustGMFAN">
              <FloatingLabel controlId="strCustGMFAN" label="GM FAN">
                <Form.Control type="text" ref={strCustGMFANRef} />
              </FloatingLabel>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col xs={12}>
            <Form.Group className="mb-3" controlId="addressLine1">
              <FloatingLabel controlId="addressLine1" label="Address Line 1">
                <Form.Control type="text" ref={addressLine1Ref} />
              </FloatingLabel>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col xs={12}>
            <Form.Group className="mb-3" controlId="addressLine2">
              <FloatingLabel controlId="addressLine2" label="Address Line 2">
                <Form.Control type="text" ref={addressLine2Ref} />
              </FloatingLabel>
            </Form.Group>
          </Col>
        </Row>

        {/* Row with City, State, ZIP */}
        <Row>
          <Col xs={12} md={3}>
            <Form.Group className="mb-3" controlId="city">
              <FloatingLabel controlId="city" label="City">
                <Form.Control type="text" ref={cityRef} />
              </FloatingLabel>
            </Form.Group>
          </Col>
          <Col xs={12} md={3}>
            <Form.Group className="mb-3" controlId="state">
              <FloatingLabel controlId="state" label="State">
                <Form.Control type="text" ref={stateRef} />
              </FloatingLabel>
            </Form.Group>
          </Col>
          <Col xs={12} md={3}>
            <Form.Group className="mb-3" controlId="zip">
              <FloatingLabel controlId="zip" label="ZIP Code">
                <Form.Control type="text" ref={zipRef} />
              </FloatingLabel>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col>
            <Form.Group className="mb-3" controlId="customerNotes">
              <FloatingLabel controlId="customerNotes" label="Customer Notes">
                <Form.Control as="textarea" ref={customerNotesRef} style={{ height: '100px' }} />
              </FloatingLabel>
            </Form.Group>
          </Col>
        </Row>

        <Button variant="primary" type="submit" disabled={loading || buttonsDisabled} className="mt-3">
          {customer ? 'Update Customer' : 'Add Customer'}
        </Button>
      </Form>
    </div>
  );
}

export default CustomerForm;